import Promise from 'bluebird';
import _ from 'underscore';

import { parseSync } from '@bingads-webui/safe-json';
import { MAX_DISPLAY_COUNT } from './consts';

function parseIds(ids) {
  const parsedIdMap = _.reduce(ids, (memo, id) => {
    const parsedId = parseSync(id);
    memo[parsedId.Id] = parsedId; // eslint-disable-line no-param-reassign
    return memo;
  }, {});
  const parsedIds = _.map(_.keys(parsedIdMap), id => parseInt(id, 10));

  return {
    parsedIdMap,
    parsedIds,
  };
}

function load(params, dataSource, entitySet) {
  return dataSource
    .findAll(params, {
      cacheResponse: false,
      bypassCache: true,
      entitySet,
      headers: {
        'x-ms-refreshcampaigncache': true,
      },
    });
}

export class SelectorDataSource {
  constructor({
    dataContextManager,
    cacheService,
    dataSource,
    type,
    selectorLoadAction,
    entitySet,
    encodeData,
    where,
  }) {
    this.options = {
      dataContextManager,
      cacheService,
      dataSource,
      type,
      selectorLoadAction,
      entitySet,
      encodeData,
      where,
    };
  }

  get itemsDisplayLimit() {
    return MAX_DISPLAY_COUNT;
  }

  get(viewFilter, searchFilter, ids = []) {
    const params = {
      limit: this.itemsDisplayLimit + 1,
      select: ['Id', 'Name'],
      orderBy: 'Name',
      where: this.options.where,
    };

    if (searchFilter) {
      const nameFilter = {
        Name: {
          $contains: searchFilter,
        },
      };
      if (params.where) {
        params.where = { $and: [params.where, nameFilter] };
      } else {
        params.where = nameFilter;
      }
    }

    const { parsedIds, parsedIdMap } = parseIds(ids);

    if (ids.length > 0) {
      const idQuery = {
        $or: _.map(parsedIds, id => (
          { Id: { $eq: id } }
        )),
      };
      const endQueries = [idQuery].concat(params.where || []);

      params.where = { $and: endQueries };
    }

    let cachedData;

    this.options.dataContextManager.getContext(this.options.selectorLoadAction, false, 'GET');
    const pseudoRequest = {
      url: this.options.selectorLoadAction,
      data: params,
      success: (data) => {
        cachedData = data[this.options.type];
      },
    };

    this.options.cacheService.cache(pseudoRequest);
    if (pseudoRequest.skipRequest) {
      return Promise.resolve(cachedData);
    }
    return load(params, this.options.dataSource, this.options.entitySet)
      .then((newData) => {
        let patchedData = [];

        const missingIds = _.difference(parsedIds, _.pluck(newData, 'Id'));

        _.each(missingIds, (missingId) => {
          patchedData.push(parsedIdMap[missingId]);
        });

        if (searchFilter) {
          patchedData = _.filter(
            patchedData,
            entity => searchFilter.indexOf(entity && entity.Name) >= 0
          );
        }

        const encodedData =
          _.map(patchedData.concat(newData), item => this.options.encodeData(item));

        pseudoRequest.success({ [this.options.type]: encodedData });
        return encodedData;
      });
  }

  /**
   * get entity by ids
   *
   * @param {Array[]} ids - ids to fetch, when empty, will return empty result
   * @returns {Promise} result promise
  */
  getByIds(ids) {
    if (_.size(ids) > 0) {
      const { parsedIdMap } = parseIds(ids);

      return Promise.resolve(_.map(
        _.values(parsedIdMap),
        item => this.options.encodeData(item)
      ));
    }
    return Promise.resolve([]);
  }
}
